import { useState } from "react";
import { Bell, Mail, MessageSquare, Loader2, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from "@/hooks/use-notifications";

interface PreferenceToggleProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

function PreferenceToggle({
  label,
  description,
  checked,
  onChange,
  disabled,
}: PreferenceToggleProps) {
  return (
    <label
      className={cn(
        "flex items-start gap-4 p-4 rounded-lg border transition-colors cursor-pointer",
        checked
          ? "border-primary-200 bg-primary-50/50"
          : "border-gray-200 hover:border-gray-300",
        disabled && "opacity-50 cursor-not-allowed"
      )}
    >
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900">{label}</p>
        <p className="text-sm text-gray-500 mt-0.5">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          "relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2",
          checked ? "bg-primary-600" : "bg-gray-200",
          disabled && "cursor-not-allowed"
        )}
      >
        <span
          className={cn(
            "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform",
            "absolute top-0.5",
            checked ? "translate-x-5" : "translate-x-0.5"
          )}
        />
      </button>
    </label>
  );
}

export function NotificationPreferences() {
  const { data: preferences, isLoading } = useNotificationPreferences();
  const updatePreferences = useUpdateNotificationPreferences();
  const [showSaved, setShowSaved] = useState(false);

  const handleChange = async (
    key: keyof NonNullable<typeof preferences>,
    value: boolean
  ) => {
    if (!preferences) return;

    try {
      await updatePreferences.mutateAsync({
        ...preferences,
        [key]: value,
      });

      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 2000);
    } catch {
      // Error handled by mutation
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin text-gray-400" />
      </div>
    );
  }

  if (!preferences) {
    return (
      <div className="text-center py-12 text-gray-500">
        Kunde inte ladda inställningar
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Notifikationsinställningar
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Välj hur du vill bli meddelad om viktiga händelser
          </p>
        </div>
        <div
          className={cn(
            "flex items-center gap-1.5 text-sm font-medium text-green-600 transition-opacity",
            showSaved ? "opacity-100" : "opacity-0"
          )}
        >
          <Check size={16} />
          <span>Sparat</span>
        </div>
      </div>

      {/* In-App Notifications */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-gray-900">
          <Bell size={18} />
          <h4 className="font-medium">I appen</h4>
        </div>
        <PreferenceToggle
          label="Aktivera notifikationer"
          description="Visa notifikationer i notifikationscentret"
          checked={preferences.inAppEnabled}
          onChange={(checked) => handleChange("inAppEnabled", checked)}
          disabled={updatePreferences.isPending}
        />
      </div>

      {/* Email Notifications */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-gray-900">
          <Mail size={18} />
          <h4 className="font-medium">E-post</h4>
        </div>

        <div className="space-y-3">
          <PreferenceToggle
            label="Prenumerationspåminnelser"
            description="Påminnelser om att din prenumeration snart förnyas eller löper ut"
            checked={preferences.emailSubscriptionReminders}
            onChange={(checked) =>
              handleChange("emailSubscriptionReminders", checked)
            }
            disabled={updatePreferences.isPending}
          />

          <PreferenceToggle
            label="Betalningskvitton"
            description="Bekräftelse och kvitto när en betalning genomförts"
            checked={preferences.emailPaymentReceipts}
            onChange={(checked) =>
              handleChange("emailPaymentReceipts", checked)
            }
            disabled={updatePreferences.isPending}
          />

          <PreferenceToggle
            label="Betalningsproblem"
            description="Avisering om en betalning misslyckades"
            checked={preferences.emailPaymentFailures}
            onChange={(checked) =>
              handleChange("emailPaymentFailures", checked)
            }
            disabled={updatePreferences.isPending}
          />
        </div>
      </div>

      {/* Marketing */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-gray-900">
          <MessageSquare size={18} />
          <h4 className="font-medium">Marknadsföring</h4>
        </div>

        <PreferenceToggle
          label="Nyhetsbrev och tips"
          description="Produktnyheter, tips och erbjudanden via e-post"
          checked={preferences.emailMarketing}
          onChange={(checked) => handleChange("emailMarketing", checked)}
          disabled={updatePreferences.isPending}
        />
      </div>

      {/* Info text */}
      <p className="text-xs text-gray-400">
        Viktiga meddelanden om ditt konto, som säkerhetsvarningar, skickas
        alltid via e-post oavsett dessa inställningar.
      </p>
    </div>
  );
}
